import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { FieldWrapper, ConditionalWrapper } from './styles';

type ConditionalFieldWrapperProps = {
  watchFieldName: string;
  children: React.ReactNode;
  showWhen?: boolean | string | ((value: any) => boolean);
};

// Component that conditionally shows children based on form field value
// Note: Field cleanup on unmount is handled by react-hook-form's shouldUnregister option
export const ConditionalFieldWrapper = ({
  watchFieldName,
  children,
  showWhen,
}: ConditionalFieldWrapperProps): JSX.Element | null => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { watch } = useFormContext();
  const fieldValue = watch(watchFieldName);

  const shouldShow = useMemo(() => {
    if (showWhen === undefined) {
      // Default behavior: show when truthy
      return !!fieldValue;
    }

    if (typeof showWhen === 'function') {
      // Custom function to determine visibility
      return showWhen(fieldValue);
    }

    // Direct value comparison
    return fieldValue === showWhen;
  }, [fieldValue, showWhen]);

  if (!shouldShow) {
    return null;
  }

  return (
    <FieldWrapper>
      <ConditionalWrapper>{children}</ConditionalWrapper>
    </FieldWrapper>
  );
};
